import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { CreateVirtualCard } from '../services/profile/ProfileService';
import { useTranslation } from 'react-i18next';
import { CreditCard } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import useI18nForm from './common/hooks/useI18nForm';
import { ProfileInfo } from './common/props-types';
import LoadingIndicator from './common/LoadingIndicator';
import { Link } from 'react-router-dom';
import i18n from 'i18n';
import { CardCreationType, CardType } from 'services/profile/constants';
import GoogleTagManager from 'common/GoogleTagManager';

const CreationCard = (props: {
  profileAuth: { sessionId: string; token: string };
  stepHandler: (value: number | ((prevVar: number) => number)) => void;
  currentStep: number;
  profileHandler: (value: ProfileInfo | ((prevVar: ProfileInfo) => ProfileInfo)) => void;
  profile: ProfileInfo;
  isLoading: boolean;
  updatesNewsletterConsent: boolean;
  disabled?: boolean;
  isCreatingAccount: boolean;
}): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { t } = useTranslation('component');

  const disabled = props.disabled || false;

  const toggleSlider = () => {
    if (!isLoading && !props.isLoading) {
      setOpen(!open);
    }
  };

  const onClickBecomeMember = () => {
    if (props.isCreatingAccount) {
      GoogleTagManager.sendJoinFamiliplusCreate();
    }
    toggleSlider();
  };

  const HandleRadioChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.currentTarget.value == CardType.Physical.toString()) {
      formik.setFieldValue('termsAndConditionsAccepted', false);
    }
    formik.setFieldValue('selectedCardOption', event.currentTarget.value);
  };

  const validationSchema = yup.object({
    termsAndConditionsAccepted: yup.boolean().when('selectedCardOption', {
      is: (v: string) => v == CardType.Virtual.toString(),
      then: yup.boolean().isTrue(t('FormValidations.TermsAndConditionRequired').toString()),
    }),
    selectedCardOption: yup.string().required(),
  });

  const formik = useFormik({
    initialValues: {
      termsAndConditionsAccepted: false,
      selectedCardOption: CardType.Virtual.toString(),
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      if (!isLoading && !props.isLoading) {
        setIsLoading(true);
        try {
          setShowAlert(false);
          if (values.selectedCardOption == CardType.Virtual.toString()) {
            const response = await CreateVirtualCard(
              props.profileAuth,
              props.updatesNewsletterConsent,
              values.termsAndConditionsAccepted
            );
            if (!response.ok) {
              setShowAlert(true);
            } else {
              if (props.isCreatingAccount) {
                GoogleTagManager.sendJoinFamiliplusSubmit(CardCreationType.Create, CardType.Virtual);
              }
              props.stepHandler(props.currentStep + 0.5);
            }
          } else if (values.selectedCardOption == CardType.Physical.toString()) {
            if (props.isCreatingAccount) {
              GoogleTagManager.sendJoinFamiliplusSkip();
            }
            props.stepHandler(props.currentStep + 1);
          }
        } catch (e) {
          setShowAlert(true);
          return;
        } finally {
          setIsLoading(false);
        }
      }
    },
  });

  useI18nForm({ errors: formik.errors, touched: formik.touched, setFieldTouched: formik.setFieldTouched });

  const sideBox = () => (
    <Box sx={{ width: { xs: '80vw', sm: '480px' }, padding: { xs: '20px', sm: '32px' } }}>
      <form onSubmit={formik.handleSubmit}>
        <FormControl>
          <Typography variant="title">{t('BecomeAFamiliplusMember.Title')}</Typography>

          <Alert sx={{ display: showAlert ? 'flex' : 'none' }} severity="error">
            {t('Errors.CreateAccountServerError')}
          </Alert>

          <FormLabel sx={{ fontWeight: 'bold' }} id="cardTypeGroup">
            {t('BecomeAFamiliplusMember.SubTitle')}
          </FormLabel>
          <RadioGroup
            name={'selectedCardOption'}
            value={formik.values.selectedCardOption}
            onChange={HandleRadioChanged}>
            <FormControlLabel
              value={CardType.Virtual.toString()}
              control={<Radio />}
              label={t('BecomeAFamiliplusMember.VirtualCard')}
            />
            <FormControlLabel
              value={CardType.Physical.toString()}
              control={<Radio />}
              label={t('BecomeAFamiliplusMember.PhysicalCard')}
            />
            {formik.values.selectedCardOption == CardType.Physical.toString() && (
              <Alert severity="info">{t('BecomeAFamiliplusMember.PhysicalCardInfo')}</Alert>
            )}
          </RadioGroup>

          {formik.values.selectedCardOption == CardType.Virtual.toString() && (
            <FormControl
              error={formik.touched.termsAndConditionsAccepted && Boolean(formik.errors.termsAndConditionsAccepted)}>
              <FormControlLabel
                control={<Checkbox checked={formik.values.termsAndConditionsAccepted} />}
                name="termsAndConditionsAccepted"
                id="termsAndConditionsAccepted"
                onChange={formik.handleChange}
                sx={{ paddingTop: '12px' }}
                label={
                  <Typography sx={{ position: 'relative', top: '12px' }}>
                    {t('BecomeAFamiliplusMember.IRead')}{' '}
                    <Link
                      style={{ color: 'black' }}
                      target="_blank"
                      to={
                        i18n.resolvedLanguage == 'fr'
                          ? 'https://www.familiprix.com/fr/pages/termes-et-conditions-familiplus'
                          : 'https://www.familiprix.com/en/pages/terms-and-conditions-familiplus'
                      }>
                      {t('BecomeAFamiliplusMember.TermsAndConditions')}
                    </Link>{' '}
                    {t('BecomeAFamiliplusMember.Mandatory')}
                  </Typography>
                }
              />
              {formik.touched.termsAndConditionsAccepted && Boolean(formik.errors.termsAndConditionsAccepted) && (
                <FormHelperText>
                  {formik.touched.termsAndConditionsAccepted && formik.errors.termsAndConditionsAccepted}
                </FormHelperText>
              )}
            </FormControl>
          )}
        </FormControl>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="text" onClick={toggleSlider} disabled={isLoading}>
            {t('Buttons.Cancel')}
          </Button>
          {isLoading && <LoadingIndicator />}
          <Button variant="contained" type="submit" disabled={isLoading}>
            {t('Buttons.Confirm')}
          </Button>
        </Box>
      </form>
    </Box>
  );
  return (
    <>
      <Box id="CreationCard" className="boxGray" sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography pr={'60px'}>
          {t('BecomeAFamiliplusMember.DontHaveACard')} <br />
          {t('BecomeAFamiliplusMember.CreateOneHere')}
        </Typography>
        <Button
          variant="contained"
          size="large"
          color="error"
          disabled={props.isLoading || disabled}
          onClick={onClickBecomeMember}
          startIcon={<CreditCard />}
          sx={{ minWidth: '185px', ml: '16px' }}>
          {t('BecomeAFamiliplusMember.CreateCard')}
        </Button>
      </Box>
      <Drawer open={open} anchor="right" onClose={toggleSlider}>
        {sideBox()}
      </Drawer>
    </>
  );
};

export default CreationCard;
